import { Box, Typography } from '@mui/material';
import React from 'react';
import { NACHWEIS_POSITION_STATUS, PLATZHALTER_TEXT, PROJEKTDURCHFUERUNG } from 'src/lib/constants/constant';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';

import { get } from 'lodash';
import SummaryLine from 'src/components/ui-component/form-component/abschluss-page/SummaryLine';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import Schema from 'src/lib/paths/mittelabruf_FULL_Paths.json';
import NachweisPositionSchema from 'src/lib/paths/nachweisposition_FULL_Paths.json';
import { DisplayDecimal, DisplayEuroMUI } from 'src/utils/formats';
import { getFieldNamefromDataPath } from 'src/utils/helper';
const MA_P = Schema.attributes;
const BJ_P = MA_P.Foerderantrag.schema.attributes.Bewilligung_Jahresaufteilung.schema.attributes;
const NP_P = NachweisPositionSchema.attributes;

export const MittelabrufEpz = () => {
	const { application } = useApplication();
	const anzahlMonate =
		(
			get(
				application,
				MA_P.Foerderantrag.schema.attributes.Projektbeschreibung_Entwicklungszusammenarbeit.schema.attributes.Projektdurchfuehrung.path,
				'',
			) === PROJEKTDURCHFUERUNG.inland
		) ?
			'drei'
		:	'sechs';
	const haushaltsjahr = Number(get(application, MA_P.Allgemein_Haushaltsjahr.path, 0));
	const bewilligungJahresaufteilung = get(application, MA_P.Foerderantrag.schema.attributes.Bewilligung_Jahresaufteilung.path, []);
	const jahresEintrag = bewilligungJahresaufteilung.find(
		(curr: any) => Number(get(curr, getFieldNamefromDataPath(BJ_P.Projektjahr.path), 0)) === haushaltsjahr,
	);
	const foerderquote =
		Number(get(jahresEintrag, getFieldNamefromDataPath(BJ_P.Foerderquote_403741.path), 0)) ||
		Number(get(application, MA_P.Foerderantrag.schema.attributes.Allgemein_Foerderquote_Gesamt.path, 0)) ||
		0;
	const foerderung = Number(get(application, MA_P.Ermittlung_Des_Auszahlungsbetrages_Bewilligte_Zuwendung.path, 0)) || 0;
	const bereitsAusgezahlt = Number(get(application, MA_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, 0)) || 0;
	const maxAbrufbareFoerderung = Math.max(0, foerderung - bereitsAusgezahlt);
	const nachweispositionen = get(application, MA_P.Mittelabruf_Nachweispositions.path, []);
	const getNachweispositionByStatus = (status: string) => {
		return (nachweispositionen as any[]).find((eachPosition: any) => get(eachPosition, NP_P.Allgemein_Status.path) === status);
	};
	const projektausgabenAngefallen = Number(
		get(getNachweispositionByStatus(NACHWEIS_POSITION_STATUS.bereitsAngefalleneAusgaben), NP_P.Allgemein_Ausgaben_Kosten.path, 0),
	);

	const projektausgabenGeplant = Number(get(getNachweispositionByStatus(NACHWEIS_POSITION_STATUS.geplant), NP_P.Allgemein_Ausgaben_Kosten.path, 0));
	const projektausgabenGesamt = projektausgabenAngefallen + projektausgabenGeplant;
	const zuwendungAnteilig = Number(get(application, MA_P.Zuwendung_Anteilig_Gesamt.path, 0)) || 0;
	const berechneteMaxFoerderung = Number(get(application, MA_P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path, 0)) || 0;
	const verbleibendeFoerderungNachDiesemAbruf = Math.max(0, maxAbrufbareFoerderung - berechneteMaxFoerderung);
	const wurdeGekuerzt = zuwendungAnteilig - bereitsAusgezahlt > maxAbrufbareFoerderung;
	return (
		<>
			<ApplicationHeading title='Mittelabruf' description={PLATZHALTER_TEXT} />
			{wurdeGekuerzt && (
				<MUIAlert
					infoType='warning'
					message='Der berechnete Mittelabruf übersteigt die verbleibende Förderung. Es kann maximal die noch verfügbare Förderung abgerufen werden.'
				/>
			)}
			<Typography variant='body1' sx={{ mb: 2, mt: 2 }}>
				Die Berechnung des Mittelabrufs basiert auf den von Ihnen gemachten Angaben, wird automatisch generiert und steht unter dem Vorbehalt
				einer fachlichen Prüfung durch die Hessen Agentur.
			</Typography>
			<Box sx={{ mb: 2 }}>
				<SummaryLine label='Projektausgaben' value={DisplayEuroMUI(projektausgabenGesamt)} boldLabel={true} alignValueRight={true} />
				<SummaryLine
					label='bis jetzt angefallene Projektausgaben'
					value={DisplayEuroMUI(projektausgabenAngefallen)}
					alignValueRight={true}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					valueFontWeight={400}
				/>
				<SummaryLine
					label={`in den nächsten ${anzahlMonate} Monaten anfallende Projektausgaben`}
					value={DisplayEuroMUI(projektausgabenGeplant)}
					alignValueRight={true}
					labelPadding={'0.75rem 0 0.75rem 2rem'}
					valueFontWeight={400}
				/>
				<SummaryLine label='Förderquote' value={`${DisplayDecimal(foerderquote)} %`} boldLabel={true} alignValueRight={true} />
				<SummaryLine
					label='berechnete max. Förderung / Mittelabruf (vorbehaltlich einer fachlichen Prüfung durch die Hessen Agentur)'
					value={<>{DisplayEuroMUI(zuwendungAnteilig)}</>}
					valueFontWeight={400}
					alignValueRight={true}
				/>
				<SummaryLine
					label='abzüglich bereits abgerufener Zuwendungen im gleichen Haushaltsjahr'
					value={DisplayEuroMUI(bereitsAusgezahlt)}
					alignValueRight={true}
					valueFontWeight={400}
				/>
				<SummaryLine
					label='Mittelabruf'
					value={
						<>
							{DisplayEuroMUI(berechneteMaxFoerderung)}
							{wurdeGekuerzt && (
								<>
									<br />
									<Typography component='span' variant='caption' sx={{ display: 'inline-block', mt: 0.5, fontWeight: 400 }}>
										Hinweis: Der Abruf wurde auf die maximal abrufbare Zuwendung gekürzt.
									</Typography>
								</>
							)}
						</>
					}
					showBackground={true}
					alignValueRight={true}
					boldLabel={true}
				/>
			</Box>
			<Box sx={{ mb: 2, mt: 6 }}>
				<SummaryLine label='Förderung (€)' value={DisplayEuroMUI(foerderung)} boldLabel={true} alignValueRight={true} />
				<SummaryLine
					label='Verfügbare Fördermittel nach diesem Mittelabruf (€)'
					value={DisplayEuroMUI(verbleibendeFoerderungNachDiesemAbruf)}
					boldLabel={true}
					alignValueRight={true}
				/>
			</Box>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				Ausgaben können nur bis zur im Förderbescheid festgesetzten Ausgabenhöhe als zuwendungsfähig anerkannt werden können. Mögliche
				Mehrausgaben gehen zu Lasten des Zuwendungsempfängers.
				<br />
				Dieser Mittelabruf wurde automatisch durch das digitale Antragsportal der Innovationsförderung Hessen – basierend auf den von Ihnen
				gemachten Angaben – erstellt und steht unter dem Vorbehalt einer fachlichen Prüfung durch die Hessen Agentur.
			</Typography>
			<Typography variant='body2' sx={{ mt: 2, display: 'block' }}>
				<strong>Grundsätzlich gilt:</strong> Der Mittelabruf kann maximal in Höhe der bewilligten Förderung erfolgen.
			</Typography>
		</>
	);
};
